import * as React from 'react';
import BPromise from 'bluebird';
import moment from 'moment';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { ActionItem } from 'Src/components/ActionsButton/ActionsButton';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { DATE_FORMAT } from 'Src/consts/date';
import {
  ColumnDefinition,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import {
  getAllSuperAdminSchoolActionDescriptors,
  getSuperAdminActionDescriptor,
  getSuperAdminSchoolActionDescriptors,
  getSuperAdminSchoolActionDescriptorsCount,
  SuperAdminActionDescriptor
} from 'Src/helpers/service/superadmin/actionDescriptors';
import './SuperAdminSchoolActionDescriptors.css';

const NOTIFICATION_MODE_OPTIONS = [
  { text: 'Auto', value: 'AUTO' },
  { text: 'Manual', value: 'MANUAL' },
  { text: 'Scheduler', value: 'SCHEDULER' },
  { text: 'Disabled', value: 'DISABLED' }
];

const EMISSION_STATUS_OPTIONS = [
  { text: 'Not emitted', value: 'NOT_EMITTED' },
  { text: 'Emitted', value: 'EMITTED' },
  { text: 'Cancelled', value: 'CANCELLED' },
  { text: 'Failed', value: 'FAILED' }
];

const LIST_STATUS_OPTIONS = [
  { text: 'Not ready', value: 'NOT_READY' },
  { text: 'Ready', value: 'READY' },
  { text: 'Error', value: 'ERROR' }
];

function formatDateTime(value?: string): string {
  if (!value) {
    return '';
  }

  const date = moment(value);
  return date.isValid() ? date.format('DD-MM-YYYY HH:mm') : '';
}

function formatKind(item: SuperAdminActionDescriptor): string {
  return item.kind || '';
}

function formatNotificationMode(item: SuperAdminActionDescriptor): string {
  return item.notificationMode || '';
}

function formatEmissionStatus(item: SuperAdminActionDescriptor): string {
  return item.notificationEmissionStatus || '';
}

function formatAffectedUsersStatus(item: SuperAdminActionDescriptor): string {
  return item.affectedUserListStatus || '';
}

function formatScheduledDate(item: SuperAdminActionDescriptor): string {
  return formatDateTime(item.scheduledDate);
}

function formatCreatedAt(item: SuperAdminActionDescriptor): string {
  return formatDateTime(item.createdAt);
}

function formatEntity(item: SuperAdminActionDescriptor): string {
  if (item.eventId) {
    return `Event: ${item.eventId}`;
  }

  if (item.tournamentId) {
    return `Tournament: ${item.tournamentId}`;
  }

  if (item.programmeId) {
    return `Programme: ${item.programmeId}`;
  }

  return '';
}

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Kind',
    field: 'kind',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: formatKind
  },
  {
    text: 'Mode',
    field: 'notificationMode',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: formatNotificationMode
  },
  {
    text: 'Emission status',
    field: 'notificationEmissionStatus',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: formatEmissionStatus
  },
  {
    text: 'Affected users',
    field: 'affectedUserListStatus',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: formatAffectedUsersStatus
  },
  {
    text: 'Scheduled date',
    field: 'scheduledDate',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: formatScheduledDate
  },
  {
    text: 'Created',
    field: 'createdAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: formatCreatedAt
  },
  {
    text: 'Entity',
    field: 'entity',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: formatEntity
  },
  {
    text: 'Description',
    field: 'additionalDescription',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['additionalDescription']
  }
];

interface Props {
  schoolId: string;
  history: History;
  location: Location;
}

interface State {
  items: SuperAdminActionDescriptor[];
  filterOptions: any;
  currentPage: number;
  selectedItems: SuperAdminActionDescriptor[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  errorMessage: string;
  isDetailsModalOpen: boolean;
  isDetailsLoading: boolean;
  detailsErrorMessage: string;
  selectedDescriptorDetails?: SuperAdminActionDescriptor;
}

function removeEmptyAnd(where: any): any {
  const whereCopy = { ...where };

  if (Array.isArray(whereCopy.$and) && whereCopy.$and.length === 0) {
    delete whereCopy.$and;
  }

  return whereCopy;
}

function getFiltersWithoutDescriptorDateIntervals(filters: any): any {
  const filtersCopy = { ...filters };

  delete filtersCopy.createdAt;
  delete filtersCopy.scheduledDate;

  return filtersCopy;
}

function addDateIntervalWhere(where: any, filters: any, field: string): void {
  const filter = filters[field];

  if (typeof filter !== 'object' || filter === null) {
    return;
  }

  if (typeof filter.to !== 'undefined' && filter.to !== '') {
    const toFormatted = moment(filter.to, DATE_FORMAT).toDate();
    toFormatted.setHours(23);
    toFormatted.setMinutes(59);

    where[field] = { ...where[field] };
    where[field].$lte = toFormatted.toISOString();
  }

  if (typeof filter.from !== 'undefined' && filter.from !== '') {
    const fromFormatted = moment(filter.from, DATE_FORMAT).toDate();

    where[field] = { ...where[field] };
    where[field].$gte = fromFormatted.toISOString();
  }
}

export class SuperAdminSchoolActionDescriptors extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      filterOptions: this.getFilterOptions(),
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      errorMessage: '',
      isDetailsModalOpen: false,
      isDetailsLoading: false,
      detailsErrorMessage: '',
      selectedDescriptorDetails: undefined
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true, errorMessage: '' });
    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.location.search !== this.props.location.search || nextProps.schoolId !== this.props.schoolId) {
      this.setState({ isLoading: true, errorMessage: '' });
      this.setItems(nextProps);
    }
  }

  getFilterOptions() {
    return {
      notificationMode: NOTIFICATION_MODE_OPTIONS,
      notificationEmissionStatus: EMISSION_STATUS_OPTIONS,
      affectedUserListStatus: LIST_STATUS_OPTIONS
    };
  }

  getWhere(filters: any): any {
    const filtersWithoutDateIntervals = getFiltersWithoutDescriptorDateIntervals(filters);
    const where = getServerFieldSectionWhere(filtersWithoutDateIntervals, { isCorrectTime: true });

    addDateIntervalWhere(where, filters, 'createdAt');
    addDateIntervalWhere(where, filters, 'scheduledDate');

    return removeEmptyAnd(where);
  }

  setItems(props: Props = this.props) {
    const { history, schoolId } = props;
    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':') as [string, TABLE_SORT_DIRECTION];
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';
    const filters = getFilters2(COLUMNS, search);
    const isShowFilter = isFilterExist2(filters);
    const isDataFiltered = isFilterExist2(filters);
    const where = this.getWhere(filters);
    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : 'createdAt DESC';
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    return BPromise.all([
      getSuperAdminSchoolActionDescriptorsCount(schoolId, where),
      getSuperAdminSchoolActionDescriptors(schoolId, serverQueryFilter)
    ])
      .then(([countObj, items]) => {
        this.setState({
          currentPage: page,
          sortDirection,
          sortColumnsName,
          isShowFilter,
          isDataFiltered,
          filters,
          items,
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
          errorMessage: ''
        });

        return true;
      })
      .catch(error => {
        console.error('Can not load superadmin school action descriptors', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load action descriptors. Please check the session and try again.'
        });
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    const search: string[] = [];
    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== FIRST_PAGE) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    const selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({ selectedItems: selectedItemsUpdated });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;
    const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

    this.setState({
      selectedItems: [...selectedItems, ...itemsFiltered],
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;
    const where = this.getWhere(filters);

    this.setState({ isLoading: true, errorMessage: '' });

    getAllSuperAdminSchoolActionDescriptors(this.props.schoolId, where)
      .then(items => {
        const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

        this.setState({
          selectedItems: [...selectedItems, ...itemsFiltered],
          isSelectAllChecked: true,
          isLoading: false
        });
      })
      .catch(error => {
        console.error('Can not select all superadmin school action descriptors', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not select all action descriptors. Please try again.'
        });
      });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    const search: string[] = [`order=${sortField}:${order}`];

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      nextFilters = {
        ...nextFilters,
        [filterField]: filterValue
      };
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    const search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    this.setState({
      isShowFilter: !this.state.isShowFilter
    });
  };

  onDetailsModalOpen = (): void => {
    const selectedItem = this.state.selectedItems[0];

    if (this.state.selectedItems.length !== 1 || !selectedItem) {
      return;
    }

    this.setState({
      isDetailsModalOpen: true,
      isDetailsLoading: true,
      detailsErrorMessage: '',
      selectedDescriptorDetails: undefined
    });

    getSuperAdminActionDescriptor(selectedItem.id)
      .then(descriptor => {
        this.setState({
          selectedDescriptorDetails: descriptor,
          isDetailsLoading: false,
          detailsErrorMessage: ''
        });
      })
      .catch(error => {
        console.error('Can not load superadmin action descriptor details', error);
        this.setState({
          isDetailsLoading: false,
          detailsErrorMessage: 'Can not load action descriptor details. Please check the session and try again.'
        });
      });
  };

  onDetailsModalClose = (): void => {
    this.setState({
      isDetailsModalOpen: false,
      isDetailsLoading: false,
      detailsErrorMessage: '',
      selectedDescriptorDetails: undefined
    });
  };

  renderValue(value: any): string {
    if (value === null || typeof value === 'undefined') {
      return '';
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    return String(value);
  }

  renderDetailsRow(label: string, value: any) {
    const formattedValue = this.renderValue(value);

    if (formattedValue === '') {
      return null;
    }

    return (
      <div className="row mb-2" key={label}>
        <div className="col-md-4 text-muted">{label}</div>
        <div className="col-md-8 text-monospace">{formattedValue}</div>
      </div>
    );
  }

  renderJsonSection(title: string, value: any) {
    if (typeof value === 'undefined' || value === null) {
      return null;
    }

    if (Array.isArray(value) && value.length === 0) {
      return null;
    }

    if (!Array.isArray(value) && typeof value === 'object' && Object.keys(value).length === 0) {
      return null;
    }

    return (
      <div className="SuperAdminActionDescriptorDetailsSection" key={title}>
        <h6>{title}</h6>
        <pre>{JSON.stringify(value, null, 2)}</pre>
      </div>
    );
  }

  renderDetailsModal() {
    const { detailsErrorMessage, isDetailsLoading, isDetailsModalOpen, selectedDescriptorDetails } = this.state;

    if (!isDetailsModalOpen) {
      return null;
    }

    const descriptor = selectedDescriptorDetails;

    return (
      <div className="SuperAdminActionDescriptorDetailsBackdrop">
        <div className="SuperAdminActionDescriptorDetailsModal">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1">Action descriptor details</h5>
              <div className="text-muted">
                Raw descriptor data for checking scheduling, affected users and emitted notifications.
              </div>
            </div>
            <button className="btn btn-outline-secondary" onClick={this.onDetailsModalClose}>
              Close
            </button>
          </div>

          {isDetailsLoading && <Loader />}
          {detailsErrorMessage && <div className="alert alert-danger">{detailsErrorMessage}</div>}

          {!isDetailsLoading && descriptor && (
            <div>
              <div className="SuperAdminActionDescriptorDetailsSection">
                <h6>Summary</h6>
                {this.renderDetailsRow('Id', descriptor.id)}
                {this.renderDetailsRow('Kind', descriptor.kind)}
                {this.renderDetailsRow('Mode', descriptor.notificationMode)}
                {this.renderDetailsRow('Emission status', descriptor.notificationEmissionStatus)}
                {this.renderDetailsRow('Affected users status', descriptor.affectedUserListStatus)}
                {this.renderDetailsRow('Affected admins status', descriptor.affectedAdminListStatus)}
                {this.renderDetailsRow('Can emit notifications', descriptor.canEmitNotifications)}
                {this.renderDetailsRow('Scheduled date', formatDateTime(descriptor.scheduledDate))}
                {this.renderDetailsRow('Created', formatDateTime(descriptor.createdAt))}
                {this.renderDetailsRow('Updated', formatDateTime(descriptor.updatedAt))}
                {this.renderDetailsRow('School id', descriptor.schoolId)}
                {this.renderDetailsRow('Event id', descriptor.eventId)}
                {this.renderDetailsRow('Tournament id', descriptor.tournamentId)}
                {this.renderDetailsRow('Programme id', descriptor.programmeId)}
                {this.renderDetailsRow('Description', descriptor.additionalDescription)}
              </div>

              {this.renderJsonSection('Triggered by', descriptor.triggeredBy)}
              {this.renderJsonSection('Affected user list', descriptor.affectedUserList)}
              {this.renderJsonSection('Users to notify list', descriptor.usersToNotifyList)}
              {this.renderJsonSection('Affected admin list', descriptor.affectedAdminList)}
              {this.renderJsonSection('Details', descriptor.details)}
              {this.renderJsonSection('Metadata', descriptor.metadata)}
              {this.renderJsonSection('Full descriptor', descriptor)}
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const {
      currentPage,
      errorMessage,
      filters,
      filterOptions,
      isDataFiltered,
      isLoading,
      isSelectAllChecked,
      isShowFilter,
      items,
      selectedItems,
      sortColumnsName,
      sortDirection,
      total
    } = this.state;

    const actionItems: ActionItem[] = [
      {
        itemText: 'View details',
        isActive: selectedItems.length === 1,
        onItemClick: this.onDetailsModalOpen
      }
    ];

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="SuperAdminSchoolActionDescriptors">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Action descriptors</h4>
            <div className="text-muted">
              Read-only list for checking scheduled reminder descriptors, cancelled descriptors and emission statuses.
            </div>
          </div>
          <button className="btn btn-outline-secondary" onClick={() => this.setItems()}>
            Refresh
          </button>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

        {this.renderDetailsModal()}

        <Grid2
          dataItems={items}
          filters={filters}
          currentPage={currentPage}
          total={total}
          isSelectAllChecked={isSelectAllChecked}
          selectedItemsCount={selectedItems.length}
          isDataFiltered={isDataFiltered}
          sortDirection={sortDirection}
          sortColumnsName={sortColumnsName}
          isShowFilter={isShowFilter}
          dataItemsSelected={selectedItems}
          columns={COLUMNS}
          actionItems={actionItems}
          options={filterOptions}
          onItemClick={this.onItemClick}
          onSortClick={this.onTableSortClick}
          onApplyFilterClick={this.onApplyFilterClick}
          onClearFilterClick={this.onClearFilterClick}
          onTableFilterChange={this.onTableFilterChange}
          onTableFilterClick={this.onTableFilterClick}
          setCurrentPageParams={this.setCurrentPageParams}
          onSelectAllClick={this.onSelectAllClick}
          onSelectAllOnPageClick={this.onSelectAllOnPageClick}
          onUnselectAllClick={this.onUnselectAllClick}
          isCSV={true}
        />
      </div>
    );
  }
}



